/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { IInspectorControlProps } from "./InspectorTypes";
import {
	ISchemaConstMapModel,
	ISchemaConstMapModelEntry,
	TGenericBlueprintSchema
} from "@adapptio/lib-blueprint";
import {
	InspectPanel,
	InspectPanelContent,
	InspectPanelHeader,
	InspectPanelHeaderInfo,
	InspectPanelHeaderInfoTitle
} from "../InspectPanel";
import { useTranslate } from "@adapptio/lib-components";
import { termsEditorInspector } from "../../../terms/inspector";
import { ToolbarButton } from "../Toolbar/ToolbarButton";
import { Inspector } from "../../../inspector";
import { InspectorTitle } from "./InspectorTitle";
import { useBlueprintModel, useCommand } from "../../hooks";

export interface IInspectMapEntryProps extends IInspectorControlProps {
	model: ISchemaConstMapModel<TGenericBlueprintSchema>;
	modelEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>;
	title?: string;
}

export const InspectMapEntry: React.FC<IInspectMapEntryProps> = ({ title, model, modelEntry }) => {
	const t = useTranslate();

	useBlueprintModel(modelEntry.key);
	useBlueprintModel(modelEntry.value);

	const handleDelete = useCallback(() => {
		const index = model.items.findIndex((item) => item.key.nodeId === modelEntry.key.nodeId);

		if (index > -1) {
			model.schema.removeElement(model, index, true);
		}
	}, [ model, modelEntry ]);

	const [ duplicateMapModel ] = useCommand("duplicate.mapModel");

	const handleDuplicate = useCallback(() => {
		duplicateMapModel(model, modelEntry);
	}, [ model, modelEntry ]);

	return (
		<InspectPanel>
			<InspectPanelHeader>
				<InspectPanelHeaderInfo>
					<InspectPanelHeaderInfoTitle>{title}</InspectPanelHeaderInfoTitle>

					<ToolbarButton
						icon="mdi/content-copy"
						tooltip={t("editor", termsEditorInspector.component.duplicate)}
						onClick={handleDuplicate}
					/>
					<ToolbarButton
						icon="mdi/delete"
						tooltip={t("editor", termsEditorInspector.component.delete)}
						onClick={handleDelete}
					/>
				</InspectPanelHeaderInfo>

				<InspectorTitle label="name" model={modelEntry.key} />
			</InspectPanelHeader>
			<InspectPanelContent>
				<Inspector model={modelEntry.value} />
			</InspectPanelContent>
		</InspectPanel>
	);
};

InspectMapEntry.defaultProps = {
	title: ""
};
